import { type FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import type { OrganicDateFilters } from "src/dashboards/organicMedia/components/Tabs/FacebookContentReport";
import OrganicDateRangePicker from "src/dashboards/organicMedia/components/Tabs/OrganicDateRangePicker";
import LoadingSpinner from "src/components/reusable/LoadingSpinner";
import { renderUniqueDashboardKpiLucideIcons } from "src/utils/dashboardKpiLucideIcon";
import {
  type GoogleAdsAdGroupChartPerformanceItem,
  type GoogleAdsAdNetworkBreakdownItem,
  type GoogleAdsAdsSort,
  type GoogleAdsAdGroupSort,
  type GoogleAdsCampaignItem,
  type GoogleAdsCampaignSort,
  type GoogleAdsKpiItem,
  type GoogleAdsPaginationMeta,
  type GoogleAdsPerformanceByAdItem,
  type GoogleAdsPerformanceTrendItem,
  type GoogleAdsRoasByCampaignItem,
  type GoogleAdsSpendByAdGroupItem,
  type GoogleAdsSpendByCampaignItem,
  type GoogleAdsAdGroupItem,
  googleAdsDashboardService,
} from "src/services/GoogleAdsDashboardAPI";
import { getApiLangFromI18n } from "src/utils/apiLang";
import { useDashboardPdfRowOverride } from "src/hooks/useDashboardPdfTableExpansion";
import { fetchAllPagesForExport } from "src/utils/pdfExport/fetchAllPagesForExport";
import { downloadCsv } from "../../utils/paidMediaCsvExport";
import { getNumberLocale, toArabicIndicDigitsInString } from "src/utils/numberFormat";
import {
  DashboardErrorState,
  DashboardNarrativePanel,
  DashboardExportButton,
  DashboardSectionHeader,
  toggleTableSort,
  type DefinitionTooltipInfo,
  type SortDirection,
} from "src/components/common";
import { useDashboardNarrative } from "src/hooks/useDashboardNarrative";
import {
  calculateCpa,
  calculateCpc,
  calculateCtr,
  formatPercent as formatPercentMock,
} from "../../data/googleAdsMockData";
import {
  extractCurrencyPrefixFromKpiItems,
  formatPaidMediaMoney,
  formatPaidMediaMoneyShort,
} from "../../utils/paidMediaCurrencyFormat";
import { normalizeGoogleAdsMoneyValue } from "../../utils/googleAdsApiMoney";
import {
  normalizeGroupedChartData,
  METRIC_COLORS as GOOGLE_METRIC_COLORS,
  type MetricKey,
  type TooltipValue,
  type TrendGranularity,
} from "../googleAds/googleAdsChartConfig";
import {
  ADS_SORT_FIELD,
  AD_GROUP_SORT_FIELD,
  CAMPAIGN_SORT_FIELD,
  CHART_TOP_ENTRIES,
  EMPTY_PAGINATION_META,
  GOOGLE_ADS_TAB_IDS,
  GOOGLE_KPI_ICON_LABELS,
  SEARCH_DEBOUNCE_MS,
  TABLE_PAGE_SIZE,
  TREND_PAGE_SIZE,
  buildGoogleAdsKpiLookup,
  emptyGoogleAdsMetrics,
  googleAdsTrendItemLabel,
  googleAdsTrendItemSpend,
  isGoogleAdsApiKpiTitlePlaceholder,
  mapAdGroupApiItem,
  mapCampaignApiItem,
  mapPerformanceByAdItem,
  missingValue,
  toApiSort,
  type GoogleAdsAdGroupSortKey,
  type GoogleAdsAdsSortKey,
  type GoogleAdsCampaignSortKey,
  type GoogleAdsLiveMetricKey,
  type GoogleAdsTab,
  type GoogleKpiCard,
  type GoogleKpiTooltipKey,
  type GoogleSectionHelpKey,
} from "../googleAds/googleAdsTableConfig";
import GoogleKpiGrid from "../googleAds/GoogleKpiGrid";
import GooglePerformanceTrendsPanel from "../googleAds/GooglePerformanceTrendsPanel";
import GoogleConversionFunnelPanel from "../googleAds/GoogleConversionFunnelPanel";
import GoogleChartsRow1 from "../googleAds/GoogleChartsRow1";
import GoogleChartsRow2 from "../googleAds/GoogleChartsRow2";
import GoogleCampaignsTable from "../googleAds/GoogleCampaignsTable";
import GoogleAdGroupsTable from "../googleAds/GoogleAdGroupsTable";
import GoogleAdsTable from "../googleAds/GoogleAdsTable";
import GoogleAlertsPanel from "../googleAds/GoogleAlertsPanel";

type GoogleAdsReportProps = {
  dateFilters: OrganicDateFilters;
};

const GoogleAdsReport: FC<GoogleAdsReportProps> = ({ dateFilters }) => {
  const { i18n, t } = useTranslation();
  const pdfContainerRef = useRef<HTMLElement>(null);
  const [isPdfCapturing, setIsPdfCapturing] = useState(false);
  const formatPercent = useCallback(
    (v: number) => formatPercentMock(v, i18n.language),
    [i18n.language]
  );
  const formatNullablePercent = useCallback(
    (value: number | null | undefined) =>
      value === null || value === undefined ? missingValue : formatPercent(value),
    [formatPercent]
  );
  const formatNullableRoas = useCallback(
    (value: number | null | undefined) => {
      if (value === null || value === undefined) {
        return missingValue;
      }
      const s = `${value.toFixed(2)}x`;
      return i18n.language?.toLowerCase().startsWith("ar") ? toArabicIndicDigitsInString(s) : s;
    },
    [i18n.language]
  );
  const [activeTab, setActiveTab] = useState<GoogleAdsTab>("campaigns");
  const [selectedCampaignType, setSelectedCampaignType] = useState("all");
  const [activeMetrics, setActiveMetrics] = useState<MetricKey[]>([
    "spend",
    "impressions",
    "clicks",
    "conversions",
  ]);
  const [trendGranularity, setTrendGranularity] = useState<TrendGranularity>("daily");
  const [currentTrendPage, setCurrentTrendPage] = useState(1);
  const [searchCampaigns, setSearchCampaigns] = useState("");
  const [debouncedSearchCampaigns, setDebouncedSearchCampaigns] = useState("");
  const [adGroupSearch, setAdGroupSearch] = useState("");
  const [debouncedSearchAdGroups, setDebouncedSearchAdGroups] = useState("");
  const [adsSearch, setAdsSearch] = useState("");
  const [debouncedSearchAds, setDebouncedSearchAds] = useState("");
  const [currentCampaignPage, setCurrentCampaignPage] = useState(1);
  const [currentAdGroupPage, setCurrentAdGroupPage] = useState(1);
  const [adsPage, setAdsPage] = useState(0);
  const [expandedCampaigns, setExpandedCampaigns] = useState<Set<string>>(() => new Set());
  const [campaignSort, setCampaignSort] = useState<{
    key: GoogleAdsCampaignSortKey;
    direction: SortDirection;
  } | null>(null);
  const [adGroupSort, setAdGroupSort] = useState<{
    key: GoogleAdsAdGroupSortKey;
    direction: SortDirection;
  } | null>(null);
  const [adsSort, setAdsSort] = useState<{
    key: GoogleAdsAdsSortKey;
    direction: SortDirection;
  } | null>(null);
  const [liveKpis, setLiveKpis] = useState<GoogleAdsKpiItem[]>([]);
  const [livePerformanceTrends, setLivePerformanceTrends] = useState<GoogleAdsPerformanceTrendItem[]>(
    []
  );
  const [livePerformanceTrendsMonthly, setLivePerformanceTrendsMonthly] = useState<
    GoogleAdsPerformanceTrendItem[]
  >([]);
  const [liveCampaigns, setLiveCampaigns] = useState<GoogleAdsCampaignItem[]>([]);
  const [campaignPaginationMeta, setCampaignPaginationMeta] =
    useState<GoogleAdsPaginationMeta>(EMPTY_PAGINATION_META);
  const [liveRoasByCampaign, setLiveRoasByCampaign] = useState<GoogleAdsRoasByCampaignItem[]>([]);
  const [liveSpendByCampaign, setLiveSpendByCampaign] = useState<GoogleAdsSpendByCampaignItem[]>([]);
  const [liveAdNetworkTypes, setLiveAdNetworkTypes] = useState<string[]>([]);
  const [liveAdGroups, setLiveAdGroups] = useState<GoogleAdsAdGroupItem[]>([]);
  const [adGroupPaginationMeta, setAdGroupPaginationMeta] =
    useState<GoogleAdsPaginationMeta>(EMPTY_PAGINATION_META);
  const [liveAdGroupChartPerformance, setLiveAdGroupChartPerformance] = useState<
    GoogleAdsAdGroupChartPerformanceItem[]
  >([]);
  const [liveSpendByAdGroup, setLiveSpendByAdGroup] = useState<GoogleAdsSpendByAdGroupItem[]>([]);
  const [liveAdNetworkBreakdown, setLiveAdNetworkBreakdown] = useState<
    GoogleAdsAdNetworkBreakdownItem[]
  >([]);
  const [liveAdsPerformance, setLiveAdsPerformance] = useState<GoogleAdsPerformanceByAdItem[]>([]);
  const [adsPaginationMeta, setAdsPaginationMeta] =
    useState<GoogleAdsPaginationMeta>(EMPTY_PAGINATION_META);
  const [isExporting, setIsExporting] = useState(false);
  const [kpiApiError, setKpiApiError] = useState("");
  const [trendsApiError, setTrendsApiError] = useState("");
  const [campaignApiError, setCampaignApiError] = useState("");
  const [adGroupApiError, setAdGroupApiError] = useState("");
  const [adsApiError, setAdsApiError] = useState("");
  const [chartsApiError, setChartsApiError] = useState("");
  const [isKpiLoading, setIsKpiLoading] = useState(true);
  const [isTrendsLoading, setIsTrendsLoading] = useState(true);
  const [isCampaignLoading, setIsCampaignLoading] = useState(true);
  const [isChartsLoading, setIsChartsLoading] = useState(true);
  const [isAdGroupLoading, setIsAdGroupLoading] = useState(false);
  const [isAdsLoading, setIsAdsLoading] = useState(false);
  const apiLang = getApiLangFromI18n(i18n);
  const numberLocale = useMemo(() => getNumberLocale(i18n.language), [i18n.language]);

  const formatOptionalNumber = useCallback(
    (
      value: number | null | undefined,
      formatter: (_v: number) => string = v => v.toLocaleString(numberLocale)
    ) => (value === null || value === undefined ? missingValue : formatter(value)),
    [numberLocale]
  );
  const formatCountMetric = useCallback(
    (value: number | null | undefined) =>
      formatOptionalNumber(value, v =>
        v.toLocaleString(numberLocale, { maximumFractionDigits: 0 })
      ),
    [formatOptionalNumber, numberLocale]
  );
  const formatConversionMetric = useCallback(
    (value: number | null | undefined) =>
      formatOptionalNumber(value, v =>
        v.toLocaleString(numberLocale, { maximumFractionDigits: 2 })
      ),
    [formatOptionalNumber, numberLocale]
  );

  const formatDateShortChart = useCallback(
    (value: string) => {
      const iso = value.length >= 10 ? value.slice(0, 10) : value;
      const parsed = dayjs(iso, "YYYY-MM-DD", true);
      if (!parsed.isValid()) {
        return value.length >= 10 ? value.slice(5, 10) : value;
      }
      if (i18n.language?.startsWith("ar")) {
        return new Intl.DateTimeFormat(numberLocale, {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        }).format(parsed.toDate());
      }
      return parsed.format("DD-MM-YYYY");
    },
    [i18n.language, numberLocale]
  );

  const formatChartAxisCompactLocalized = useCallback(
    (value: unknown) => {
      const n = Number(value);
      if (!Number.isFinite(n)) {
        return "";
      }
      return new Intl.NumberFormat(numberLocale, {
        notation: "compact",
        maximumFractionDigits: 1,
      }).format(n);
    },
    [numberLocale]
  );

  const barSeriesLabels = useMemo(
    () => ({
      spend: t("googleAdsReport.metrics.spend"),
      impressions: t("googleAdsReport.metrics.impressions"),
      clicks: t("googleAdsReport.metrics.clicks"),
      conversions: t("googleAdsReport.metrics.conversions"),
      ctr: t("googleAdsReport.metrics.ctr"),
    }),
    [t]
  );

  const clicksConversionsLegendItems = useMemo(
    () =>
      [
        { key: "clicks" as const, label: barSeriesLabels.clicks },
        { key: "conversions" as const, label: barSeriesLabels.conversions },
      ] as const,
    [barSeriesLabels]
  );

  const spendLegendItems = useMemo(
    () => [{ key: "spend" as const, label: barSeriesLabels.spend }] as const,
    [barSeriesLabels]
  );

  const googleSectionHelp = useCallback(
    (key: GoogleSectionHelpKey): DefinitionTooltipInfo => ({
      description: t(`googleAdsReport.sectionHelp.${key}.description`),
      formula: t(`googleAdsReport.sectionHelp.${key}.formula`),
    }),
    [t]
  );

  const getGoogleKpiTooltip = useCallback(
    (key: GoogleKpiTooltipKey): DefinitionTooltipInfo => {
     
      const note = t(`googleAdsReport.kpiTooltips.${key}.note`, { defaultValue: "" });
      return {
        description: t(`googleAdsReport.kpiTooltips.${key}.description`),
        formula: t(`googleAdsReport.kpiTooltips.${key}.formula`),
        ...(note ? { note } : {}),
      };
    },
    [t]
  );

  const exportCampaignsCsv = useCallback(async () => {
    setIsExporting(true);
    try {
      const sort = toApiSort<GoogleAdsCampaignSort>(campaignSort, CAMPAIGN_SORT_FIELD, "spend_desc");
      const totalPages = Math.max(1, campaignPaginationMeta.total_pages || 1);
      const responses = await Promise.all(
        Array.from({ length: totalPages }, (_, i) =>
          googleAdsDashboardService.fetchCampaigns({
            start_date: dateFilters.appliedStartDate,
            end_date: dateFilters.appliedEndDate,
            lang: apiLang,
            page: i + 1,
            page_size: TABLE_PAGE_SIZE,
            sort: sort ?? "spend_desc",
            search: debouncedSearchCampaigns || null,
          })
        )
      );
      const headers = [
        t("googleAdsReport.campaignTable.campaign"),
        t("googleAdsReport.campaignTable.campaignType"),
        t("googleAdsReport.campaignTable.startDate"),
        t("googleAdsReport.campaignTable.endDate"),
        t("googleAdsReport.campaignTable.interactions"),
        t("googleAdsReport.campaignTable.spend"),
        t("googleAdsReport.campaignTable.dailyBudget"),
        t("googleAdsReport.campaignTable.impressions"),
        t("googleAdsReport.campaignTable.clicks"),
        t("googleAdsReport.campaignTable.ctr"),
        t("googleAdsReport.campaignTable.cpc"),
        t("googleAdsReport.campaignTable.conversions"),
        t("googleAdsReport.campaignTable.cpa"),
        t("googleAdsReport.campaignTable.roas"),
      ];
      const rows: Array<Array<string | number | null | undefined>> = [];
      responses.flatMap(r => r?.campaigns ?? []).forEach(item => {
        const row = mapCampaignApiItem(item);
        rows.push([
          row.campaignName,
          row.adNetworkType ?? "",
          row.startDate ?? "",
          row.endDate ?? "",
          row.interactions,
          row.spend ?? "",
          row.budgetFormattedValue ?? "",
          row.impressions,
          row.clicks,
          row.ctr !== null ? `${(row.ctr * 100).toFixed(2)}%` : "",
          row.cpc !== null ? row.cpc : "",
          row.conversions,
          row.cpa !== null ? row.cpa : "",
          row.roas !== null ? `${row.roas.toFixed(2)}x` : "",
        ]);
      });
      downloadCsv("google-ads-campaigns.csv", headers, rows);
    } catch {
      window.alert(t("googleAdsReport.export.failed"));
    } finally {
      setIsExporting(false);
    }
  }, [apiLang, campaignPaginationMeta, campaignSort, dateFilters.appliedEndDate, dateFilters.appliedStartDate, debouncedSearchCampaigns, t]);

  const exportAdGroupsCsv = useCallback(async () => {
    setIsExporting(true);
    try {
      const sort = toApiSort<GoogleAdsAdGroupSort>(adGroupSort, AD_GROUP_SORT_FIELD, "spend_desc");
      const totalPages = Math.max(1, adGroupPaginationMeta.total_pages || 1);
      const responses = await Promise.all(
        Array.from({ length: totalPages }, (_, i) =>
          googleAdsDashboardService.fetchAdGroupPerformance({
            start_date: dateFilters.appliedStartDate,
            end_date: dateFilters.appliedEndDate,
            lang: apiLang,
            page: i + 1,
            page_size: TABLE_PAGE_SIZE,
            sort: sort ?? "spend_desc",
            search: debouncedSearchAdGroups || null,
          })
        )
      );
      const headers = [
        t("googleAdsReport.adGroupTable.adGroup"),
        t("googleAdsReport.adGroupTable.interactions"),
        t("googleAdsReport.adGroupTable.spend"),
        t("googleAdsReport.adGroupTable.impressions"),
        t("googleAdsReport.adGroupTable.clicks"),
        t("googleAdsReport.adGroupTable.ctr"),
        t("googleAdsReport.adGroupTable.cpc"),
        t("googleAdsReport.adGroupTable.conversions"),
        t("googleAdsReport.adGroupTable.cpa"),
        t("googleAdsReport.adGroupTable.roas"),
      ];
      const rows: Array<Array<string | number | null | undefined>> = [];
      responses.flatMap(r => r?.ad_groups ?? []).forEach((item, index) => {
        const row = mapAdGroupApiItem(item, index);
        rows.push([
          row.name,
          row.interactions,
          row.spend,
          row.impressions,
          row.clicks,
          row.ctr !== null ? `${(row.ctr * 100).toFixed(2)}%` : "",
          row.cpc !== null ? row.cpc : "",
          row.conversions,
          row.cpa !== null ? row.cpa : "",
          `${row.roas.toFixed(2)}x`,
        ]);
      });
      downloadCsv("google-ads-ad-groups.csv", headers, rows);
    } catch {
      window.alert(t("googleAdsReport.export.failed"));
    } finally {
      setIsExporting(false);
    }
  }, [adGroupPaginationMeta, adGroupSort, apiLang, dateFilters.appliedEndDate, dateFilters.appliedStartDate, debouncedSearchAdGroups, t]);

  const googleOverviewTabLabel = useCallback(
    (id: GoogleAdsTab) => {
      const key = id === "adgroups" ? "adGroups" : id === "alerts" ? "alertsRecs" : id;
      return t(`googleAdsReport.overviewSubTabs.${key}`);
    },
    [t]
  );

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearchCampaigns(searchCampaigns);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [searchCampaigns]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearchAdGroups(adGroupSearch);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [adGroupSearch]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearchAds(adsSearch);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [adsSearch]);

  useEffect(() => {
    let isMounted = true;

    const loadKpis = async () => {
      try {
        setIsKpiLoading(true);
        setKpiApiError("");
        const response = await googleAdsDashboardService.fetchDashboardKPI({
          start_date: dateFilters.appliedStartDate,
          end_date: dateFilters.appliedEndDate,
          lang: apiLang,
        });

        if (isMounted) {
          setLiveKpis(response?.kpis ?? []);
        }
      } catch (error) {
        if (isMounted) {
          setLiveKpis([]);
          setKpiApiError(
            error instanceof Error ? error.message : t("googleAdsReport.errors.kpiLoad")
          );
        }
      } finally {
        if (isMounted) {
          setIsKpiLoading(false);
        }
      }
    };

    if (dateFilters.appliedStartDate <= dateFilters.appliedEndDate) {
      void loadKpis();
    } else {
      setLiveKpis([]);
      setKpiApiError("");
      setIsKpiLoading(false);
    }

    return () => {
      isMounted = false;
    };
  }, [apiLang, dateFilters.appliedEndDate, dateFilters.appliedStartDate, t]);

  useEffect(() => {
    let isMounted = true;

    const loadTrends = async () => {
      try {
        setIsTrendsLoading(true);
        setTrendsApiError("");
        const response = await googleAdsDashboardService.fetchPerformanceTrends({
          start_date: dateFilters.appliedStartDate,
          end_date: dateFilters.appliedEndDate,
          lang: apiLang,
        });

        if (isMounted) {
          setLivePerformanceTrends(response?.performance_trends ?? []);
          setLivePerformanceTrendsMonthly(response?.performance_trends_monthly ?? []);
        }
      } catch (error) {
        if (isMounted) {
          setLivePerformanceTrends([]);
          setLivePerformanceTrendsMonthly([]);
          setTrendsApiError(
            error instanceof Error ? error.message : t("googleAdsReport.errors.trendsLoad")
          );
        }
      } finally {
        if (isMounted) {
          setIsTrendsLoading(false);
        }
      }
    };

    if (dateFilters.appliedStartDate <= dateFilters.appliedEndDate) {
      void loadTrends();
    } else {
      setLivePerformanceTrends([]);
      setLivePerformanceTrendsMonthly([]);
      setTrendsApiError("");
      setIsTrendsLoading(false);
    }

    return () => {
      isMounted = false;
    };
  }, [apiLang, dateFilters.appliedEndDate, dateFilters.appliedStartDate, t]);

  useEffect(() => {
    let isMounted = true;

    const loadCharts = async () => {
      try {
        setIsChartsLoading(true);
        setChartsApiError("");
        const [roasResult, typesResult] = await Promise.allSettled([
          googleAdsDashboardService.fetchRoasByCampaign({
            start_date: dateFilters.appliedStartDate,
            end_date: dateFilters.appliedEndDate,
            lang: apiLang,
          }),
          googleAdsDashboardService.fetchAdNetworkTypes({
            start_date: dateFilters.appliedStartDate,
            end_date: dateFilters.appliedEndDate,
            lang: apiLang,
          }),
        ]);

        if (!isMounted) {
          return;
        }

        setLiveRoasByCampaign(
          roasResult.status === "fulfilled" ? roasResult.value?.roas_by_campaign ?? [] : []
        );
        setLiveSpendByCampaign(
          roasResult.status === "fulfilled" ? roasResult.value?.spend_by_campaign ?? [] : []
        );
        setLiveAdNetworkTypes(
          typesResult.status === "fulfilled" ? typesResult.value?.ad_network_types ?? [] : []
        );

        const failed = [roasResult, typesResult].find(r => r.status === "rejected");
        if (failed?.status === "rejected") {
          setChartsApiError(
            failed.reason instanceof Error
              ? failed.reason.message
              : t("googleAdsReport.errors.chartsLoad")
          );
        }
      } finally {
        if (isMounted) {
          setIsChartsLoading(false);
        }
      }
    };

    if (dateFilters.appliedStartDate <= dateFilters.appliedEndDate) {
      void loadCharts();
    } else {
      setLiveRoasByCampaign([]);
      setLiveSpendByCampaign([]);
      setLiveAdNetworkTypes([]);
      setChartsApiError("");
      setIsChartsLoading(false);
    }

    return () => {
      isMounted = false;
    };
  }, [apiLang, dateFilters.appliedEndDate, dateFilters.appliedStartDate, t]);

  useEffect(() => {
    let isMounted = true;

    const loadCampaigns = async () => {
      try {
        setIsCampaignLoading(true);
        setCampaignApiError("");
        const sort = toApiSort<GoogleAdsCampaignSort>(
          campaignSort,
          CAMPAIGN_SORT_FIELD,
          "spend_desc"
        );
        const response = await googleAdsDashboardService.fetchCampaigns({
          start_date: dateFilters.appliedStartDate,
          end_date: dateFilters.appliedEndDate,
          lang: apiLang,
          page: currentCampaignPage,
          page_size: TABLE_PAGE_SIZE,
          sort: sort ?? "spend_desc",
          search: debouncedSearchCampaigns || null,
          ad_network_type: selectedCampaignType === "all" ? null : selectedCampaignType,
        });

        if (isMounted) {
          setLiveCampaigns(response?.campaigns ?? []);
          setCampaignPaginationMeta(response?.meta ?? EMPTY_PAGINATION_META);
        }
      } catch (error) {
        if (isMounted) {
          setLiveCampaigns([]);
          setCampaignPaginationMeta(EMPTY_PAGINATION_META);
          setCampaignApiError(
            error instanceof Error ? error.message : t("googleAdsReport.errors.campaignLoad")
          );
        }
      } finally {
        if (isMounted) {
          setIsCampaignLoading(false);
        }
      }
    };

    if (dateFilters.appliedStartDate <= dateFilters.appliedEndDate) {
      void loadCampaigns();
    } else {
      setLiveCampaigns([]);
      setCampaignPaginationMeta(EMPTY_PAGINATION_META);
      setCampaignApiError("");
      setIsCampaignLoading(false);
    }

    return () => {
      isMounted = false;
    };
  }, [
    apiLang,
    campaignSort,
    currentCampaignPage,
    dateFilters.appliedEndDate,
    dateFilters.appliedStartDate,
    debouncedSearchCampaigns,
    selectedCampaignType,
    t,
  ]);

  useEffect(() => {
    let isMounted = true;

    const loadAdGroups = async () => {
      try {
        setIsAdGroupLoading(true);
        setAdGroupApiError("");
        const sort = toApiSort<GoogleAdsAdGroupSort>(
          adGroupSort,
          AD_GROUP_SORT_FIELD,
          "spend_desc"
        );
        const response = await googleAdsDashboardService.fetchAdGroupPerformance({
          start_date: dateFilters.appliedStartDate,
          end_date: dateFilters.appliedEndDate,
          lang: apiLang,
          page: currentAdGroupPage,
          page_size: TABLE_PAGE_SIZE,
          sort: sort ?? "spend_desc",
          search: debouncedSearchAdGroups || null,
        });

        if (isMounted) {
          setLiveAdGroups(response?.ad_groups ?? []);
          setAdGroupPaginationMeta(response?.meta ?? EMPTY_PAGINATION_META);
        }
      } catch (error) {
        if (isMounted) {
          setLiveAdGroups([]);
          setAdGroupPaginationMeta(EMPTY_PAGINATION_META);
          setAdGroupApiError(
            error instanceof Error ? error.message : t("googleAdsReport.errors.adGroupLoad")
          );
        }
      } finally {
        if (isMounted) {
          setIsAdGroupLoading(false);
        }
      }
    };

    const loadAdGroupCharts = async () => {
      try {
        const response = await googleAdsDashboardService.fetchAdGroupCharts({
          start_date: dateFilters.appliedStartDate,
          end_date: dateFilters.appliedEndDate,
          lang: apiLang,
        });

        if (isMounted) {
          setLiveAdGroupChartPerformance(response?.ad_group_performance ?? []);
          setLiveSpendByAdGroup(response?.spend_by_ad_group ?? []);
        }
      } catch {
        if (isMounted) {
          setLiveAdGroupChartPerformance([]);
          setLiveSpendByAdGroup([]);
        }
      }
    };

    if (dateFilters.appliedStartDate <= dateFilters.appliedEndDate) {
      if (activeTab === "adgroups") {
        void loadAdGroups();
      }
      void loadAdGroupCharts();
    } else {
      setLiveAdGroups([]);
      setAdGroupPaginationMeta(EMPTY_PAGINATION_META);
      setLiveAdGroupChartPerformance([]);
      setLiveSpendByAdGroup([]);
      setAdGroupApiError("");
      setIsAdGroupLoading(false);
    }

    return () => {
      isMounted = false;
    };
  }, [
    activeTab,
    adGroupSort,
    apiLang,
    currentAdGroupPage,
    dateFilters.appliedEndDate,
    dateFilters.appliedStartDate,
    debouncedSearchAdGroups,
    t,
  ]);

  useEffect(() => {
    let isMounted = true;

    const loadAds = async () => {
      try {
        setIsAdsLoading(true);
        setAdsApiError("");
        const sort = toApiSort<GoogleAdsAdsSort>(adsSort, ADS_SORT_FIELD, "spend_desc");
        const response = await googleAdsDashboardService.fetchPerformanceByAd({
          start_date: dateFilters.appliedStartDate,
          end_date: dateFilters.appliedEndDate,
          lang: apiLang,
          page: adsPage + 1,
          page_size: TABLE_PAGE_SIZE,
          sort: sort ?? "spend_desc",
          search: debouncedSearchAds || null,
        });

        if (isMounted) {
          setLiveAdsPerformance(response?.ads ?? []);
          setAdsPaginationMeta(response?.meta ?? EMPTY_PAGINATION_META);
        }
      } catch (error) {
        if (isMounted) {
          setLiveAdsPerformance([]);
          setAdsPaginationMeta(EMPTY_PAGINATION_META);
          setAdsApiError(
            error instanceof Error ? error.message : t("googleAdsReport.errors.adsLoad")
          );
        }
      } finally {
        if (isMounted) {
          setIsAdsLoading(false);
        }
      }
    };

    if (dateFilters.appliedStartDate <= dateFilters.appliedEndDate) {
      if (activeTab === "ads") {
        void loadAds();
      }
    } else {
      setLiveAdsPerformance([]);
      setAdsPaginationMeta(EMPTY_PAGINATION_META);
      setAdsApiError("");
      setIsAdsLoading(false);
    }

    return () => {
      isMounted = false;
    };
  }, [
    activeTab,
    adsPage,
    adsSort,
    apiLang,
    dateFilters.appliedEndDate,
    dateFilters.appliedStartDate,
    debouncedSearchAds,
    t,
  ]);

  useEffect(() => {
    let isMounted = true;

    const loadAdNetworkBreakdown = async () => {
      try {
        const response = await googleAdsDashboardService.fetchAdNetworkBreakdown({
          start_date: dateFilters.appliedStartDate,
          end_date: dateFilters.appliedEndDate,
          lang: apiLang,
        });

        if (isMounted) {
          setLiveAdNetworkBreakdown(response?.ad_network_breakdown ?? []);
        }
      } catch {
        if (isMounted) {
          setLiveAdNetworkBreakdown([]);
        }
      }
    };

    if (dateFilters.appliedStartDate <= dateFilters.appliedEndDate) {
      void loadAdNetworkBreakdown();
    } else {
      setLiveAdNetworkBreakdown([]);
    }

    return () => {
      isMounted = false;
    };
  }, [apiLang, dateFilters.appliedEndDate, dateFilters.appliedStartDate]);

  const liveKpiLookup = useMemo(() => buildGoogleAdsKpiLookup(liveKpis), [liveKpis]);

  const currencyPrefix = useMemo(
    () => extractCurrencyPrefixFromKpiItems(liveKpis),
    [liveKpis]
  );
  const formatCurrency = useCallback(
    (
      amount: number,
      options?: { minimumFractionDigits?: number; maximumFractionDigits?: number }
    ) => formatPaidMediaMoney(amount, currencyPrefix, options),
    [currencyPrefix]
  );
  const formatCurrencyShort = useCallback(
    (amount: number) => formatPaidMediaMoneyShort(amount, currencyPrefix),
    [currencyPrefix]
  );
  const formatNullableCurrency = useCallback(
    (value: number | null | undefined) =>
      value === null || value === undefined ? missingValue : formatCurrency(value),
    [formatCurrency]
  );
  const formatChartMetricTooltipValue = useCallback(
    (value: TooltipValue, metricKey: string) => {
      const raw = Array.isArray(value) ? value[0] : value;
      const n = Number(raw);
      if (!Number.isFinite(n)) {
        return String(value ?? "");
      }
      if (metricKey === "spend") {
        return formatCurrency(n);
      }
      if (metricKey === "conversions") {
        return n.toLocaleString(numberLocale, { maximumFractionDigits: 2 });
      }
      if (metricKey === "impressions" || metricKey === "clicks") {
        return n.toLocaleString(numberLocale, { maximumFractionDigits: 0 });
      }
      return n.toLocaleString(numberLocale);
    },
    [formatCurrency, numberLocale]
  );

  const campaignRows = useMemo(
    () => liveCampaigns.map(mapCampaignApiItem),
    [liveCampaigns]
  );

  const campaignTypeFilterOptions = useMemo(
    () => [
      { value: "all", label: t("googleAdsReport.filters.allCampaignTypes") },
      ...liveAdNetworkTypes.map(type => ({
        value: type,
        label: type,
      })),
    ],
    [liveAdNetworkTypes, t]
  );

  useEffect(() => {
    if (selectedCampaignType === "all") {
      return;
    }
    const stillValid = campaignTypeFilterOptions.some(
      option => option.value === selectedCampaignType
    );
    if (!stillValid) {
      setSelectedCampaignType("all");
      setCurrentCampaignPage(1);
    }
  }, [campaignTypeFilterOptions, selectedCampaignType]);

  const toggleCampaignExpanded = useCallback((campaignRowId: string) => {
    setExpandedCampaigns(current => {
      const next = new Set(current);
      if (next.has(campaignRowId)) {
        next.delete(campaignRowId);
      } else {
        next.add(campaignRowId);
      }
      return next;
    });
  }, []);

  const effectiveMetrics = useMemo(() => {
    const totalSpend =
      liveKpiLookup["total spend"]?.value ?? emptyGoogleAdsMetrics.totalSpend;
    const impressions =
      liveKpiLookup.impressions?.value ?? emptyGoogleAdsMetrics.impressions;
    const clicks = liveKpiLookup.clicks?.value ?? emptyGoogleAdsMetrics.clicks;
    const conversions =
      liveKpiLookup.conversions?.value ?? emptyGoogleAdsMetrics.conversions;

    return {
      totalSpend,
      impressions,
      clicks,
      conversions,
      ctr: liveKpiLookup.ctr?.value ?? calculateCtr(clicks, impressions),
      cpc: liveKpiLookup.cpc?.value ?? calculateCpc(totalSpend, clicks),
      cpa: liveKpiLookup.cpa?.value ?? calculateCpa(totalSpend, conversions),
      roas: liveKpiLookup.roas?.value ?? emptyGoogleAdsMetrics.roas,
    };
  }, [liveKpiLookup]);

  const funnelSteps = useMemo(
    () =>
      [
        {
          kind: "currency" as const,
          label: t("googleAdsReport.metrics.spend"),
          value: Math.round(effectiveMetrics.totalSpend),
          color: GOOGLE_METRIC_COLORS.spend,
        },
        {
          kind: "count" as const,
          label: t("googleAdsReport.metrics.impressions"),
          value: effectiveMetrics.impressions,
          color: GOOGLE_METRIC_COLORS.impressions,
        },
        {
          kind: "count" as const,
          label: t("googleAdsReport.metrics.clicks"),
          value: effectiveMetrics.clicks,
          color: GOOGLE_METRIC_COLORS.clicks,
        },
        {
          kind: "conversion" as const,
          label: t("googleAdsReport.metrics.conversions"),
          value: effectiveMetrics.conversions,
          color: GOOGLE_METRIC_COLORS.conversions,
        },
      ].sort((a, b) => b.value - a.value),
    [effectiveMetrics, t]
  );

  const activeTrendSeries = useMemo(
    () =>
      trendGranularity === "monthly"
        ? livePerformanceTrendsMonthly
        : livePerformanceTrends,
    [livePerformanceTrends, livePerformanceTrendsMonthly, trendGranularity]
  );

  const performanceTrendData = useMemo(() => {
    if (activeTrendSeries.length > 0) {
      return [...activeTrendSeries]
        .map((row, index) => {
          const label =
            googleAdsTrendItemLabel(row) ||
            `${dateFilters.appliedStartDate}#${index}`;
          return {
            label,
            spend: googleAdsTrendItemSpend(row),
            impressions: row.impressions ?? 0,
            clicks: row.clicks ?? 0,
            conversions: row.conversions ?? 0,
          };
        })
        .sort((a, b) => a.label.localeCompare(b.label));
    }

    const fallbackPoint = {
      spend: effectiveMetrics.totalSpend,
      impressions: effectiveMetrics.impressions,
      clicks: effectiveMetrics.clicks,
      conversions: effectiveMetrics.conversions,
    };
    return [
      { label: dateFilters.appliedStartDate, ...fallbackPoint },
      { label: dateFilters.appliedEndDate, ...fallbackPoint },
    ];
  }, [
    activeTrendSeries,
    dateFilters.appliedEndDate,
    dateFilters.appliedStartDate,
    effectiveMetrics.clicks,
    effectiveMetrics.conversions,
    effectiveMetrics.impressions,
    effectiveMetrics.totalSpend,
  ]);

  const hasSinglePerformanceTrendPoint = performanceTrendData.length === 1;

  const trendTotalPages = Math.max(1, Math.ceil(performanceTrendData.length / TREND_PAGE_SIZE));
  const paginatedTrendData = useMemo(
    () =>
      performanceTrendData.slice(
        (currentTrendPage - 1) * TREND_PAGE_SIZE,
        currentTrendPage * TREND_PAGE_SIZE
      ),
    [currentTrendPage, performanceTrendData]
  );

  useEffect(() => {
    setCurrentTrendPage(1);
  }, [dateFilters.appliedEndDate, dateFilters.appliedStartDate, trendGranularity]);

  useEffect(() => {
    setCurrentTrendPage(page => Math.min(page, trendTotalPages));
  }, [trendTotalPages]);

  const kpiChangePercent = useCallback(
    (key: GoogleAdsLiveMetricKey): number | undefined => {
      const item = liveKpiLookup[key];
      const raw = item?.percent_change ?? item?.change_percent;
      if (raw === undefined || raw === null) {
        return undefined;
      }
      const n = Number(raw);
      return Number.isFinite(n) ? n : undefined;
    },
    [liveKpiLookup]
  );

  const kpiChangeFormatted = useCallback(
    (key: GoogleAdsLiveMetricKey): string | undefined => {
      const raw = liveKpiLookup[key]?.percent_changeFormattedValue;
      return typeof raw === "string" && raw.trim() ? raw.trim() : undefined;
    },
    [liveKpiLookup]
  );

  const kpis = useMemo((): GoogleKpiCard[] => {
    const titleFor = (liveKey: GoogleAdsLiveMetricKey, tooltipKey: GoogleKpiTooltipKey) => {
      const raw = liveKpiLookup[liveKey]?.title?.trim();
      if (raw && raw.length > 0 && !isGoogleAdsApiKpiTitlePlaceholder(raw)) {
        return raw;
      }
      return t(`googleAdsReport.kpis.${tooltipKey}`);
    };
    const valueFor = (
      liveKey: GoogleAdsLiveMetricKey,
      fallback: string
    ) => liveKpiLookup[liveKey]?.formatted_value ?? fallback;

    return [
      {
        liveKey: "total spend",
        tooltipKey: "totalSpend",
        title: titleFor("total spend", "totalSpend"),
        value: valueFor("total spend", formatCurrency(effectiveMetrics.totalSpend)),
        change: kpiChangePercent("total spend"),
        changeFormattedValue: kpiChangeFormatted("total spend"),
        trend: [],
        good: true,
        polarity: "higher-is-better",
      },
      {
        liveKey: "impressions",
        tooltipKey: "impressions",
        title: titleFor("impressions", "impressions"),
        value: valueFor("impressions", formatCountMetric(effectiveMetrics.impressions)),
        change: kpiChangePercent("impressions"),
        changeFormattedValue: kpiChangeFormatted("impressions"),
        trend: [],
        good: true,
        polarity: "higher-is-better",
      },
      {
        liveKey: "clicks",
        tooltipKey: "clicks",
        title: titleFor("clicks", "clicks"),
        value: valueFor("clicks", formatCountMetric(effectiveMetrics.clicks)),
        change: kpiChangePercent("clicks"),
        changeFormattedValue: kpiChangeFormatted("clicks"),
        trend: [],
        good: true,
        polarity: "higher-is-better",
      },
      {
        liveKey: "conversions",
        tooltipKey: "conversions",
        title: titleFor("conversions", "conversions"),
        value: valueFor("conversions", formatConversionMetric(effectiveMetrics.conversions)),
        change: kpiChangePercent("conversions"),
        changeFormattedValue: kpiChangeFormatted("conversions"),
        trend: [],
        good: true,
        polarity: "higher-is-better",
      },
      {
        liveKey: "ctr",
        tooltipKey: "ctr",
        title: titleFor("ctr", "ctr"),
        value: valueFor("ctr", formatPercent(effectiveMetrics.ctr)),
        change: kpiChangePercent("ctr"),
        changeFormattedValue: kpiChangeFormatted("ctr"),
        trend: [],
        good: true,
        polarity: "higher-is-better",
      },
      {
        liveKey: "cpc",
        tooltipKey: "cpc",
        title: titleFor("cpc", "cpc"),
        value: valueFor("cpc", formatCurrencyShort(effectiveMetrics.cpc)),
        change: kpiChangePercent("cpc"),
        changeFormattedValue: kpiChangeFormatted("cpc"),
        trend: [],
        good: true,
        polarity: "lower-is-better",
      },
      {
        liveKey: "cpa",
        tooltipKey: "cpa",
        title: titleFor("cpa", "cpa"),
        value: valueFor("cpa", formatCurrencyShort(effectiveMetrics.cpa)),
        change: kpiChangePercent("cpa"),
        changeFormattedValue: kpiChangeFormatted("cpa"),
        trend: [],
        good: true,
        polarity: "lower-is-better",
      },
      {
        liveKey: "roas",
        tooltipKey: "roas",
        title: titleFor("roas", "roas"),
        value: valueFor("roas", `${effectiveMetrics.roas.toFixed(2)}x`),
        change: kpiChangePercent("roas"),
        changeFormattedValue: kpiChangeFormatted("roas"),
        trend: [],
        good: true,
        polarity: "higher-is-better",
      },
      {
        liveKey: "impressions to clicks drop off",
        tooltipKey: "dropOffImpressionsClicks",
        title: titleFor("impressions to clicks drop off", "dropOffImpressionsClicks"),
        value: valueFor(
          "impressions to clicks drop off",
          formatPercent(
            calculateCtr(
              effectiveMetrics.impressions - effectiveMetrics.clicks,
              effectiveMetrics.impressions
            )
          )
        ),
        change: kpiChangePercent("impressions to clicks drop off"),
        changeFormattedValue: kpiChangeFormatted("impressions to clicks drop off"),
        trend: [],
        good: true,
        polarity: "lower-is-better",
      },
      {
        liveKey: "clicks to conversions drop off",
        tooltipKey: "dropOffClicksConversions",
        title: titleFor("clicks to conversions drop off", "dropOffClicksConversions"),
        value: valueFor(
          "clicks to conversions drop off",
          formatPercent(
            calculateCtr(
              effectiveMetrics.clicks - effectiveMetrics.conversions,
              effectiveMetrics.clicks
            )
          )
        ),
        change: kpiChangePercent("clicks to conversions drop off"),
        changeFormattedValue: kpiChangeFormatted("clicks to conversions drop off"),
        trend: [],
        good: true,
        polarity: "lower-is-better",
      },
    ];
  }, [effectiveMetrics, formatConversionMetric, formatCountMetric, formatCurrency, formatCurrencyShort, formatPercent, kpiChangeFormatted, kpiChangePercent, liveKpiLookup, t]);
  const googleKpiIcons = useMemo(
    () => renderUniqueDashboardKpiLucideIcons(GOOGLE_KPI_ICON_LABELS, 22, "googleAds"),
    []
  );

  const adGroupRows = useMemo(
    () => liveAdGroups.map((row, index) => mapAdGroupApiItem(row, index)),
    [liveAdGroups]
  );

  const adGroupChartData = useMemo(
    () => {
      const rows = liveAdGroupChartPerformance.slice(0, CHART_TOP_ENTRIES).map(item => ({
        label: item.ad_group_name ?? "Unknown",
        spend: 0,
        impressions: 0,
        clicks: Number(item.clicks ?? 0),
        conversions: Number(item.conversions ?? 0),
        ctr: 0,
      }));
      return normalizeGroupedChartData(rows, "label");
    },
    [liveAdGroupChartPerformance]
  );

  const adGroupSpendPieData = useMemo(
    () => {
      const rows = liveSpendByAdGroup.slice(0, CHART_TOP_ENTRIES).map(item => ({
        label: item.ad_group_name ?? "Unknown",
        spend: normalizeGoogleAdsMoneyValue(item.spend).amount,
        impressions: 0,
        clicks: 0,
        conversions: 0,
        ctr: 0,
      }));
      return normalizeGroupedChartData(rows, "label");
    },
    [liveSpendByAdGroup]
  );

  const adRows = useMemo(
    () => liveAdsPerformance.map((row, index) => mapPerformanceByAdItem(row, index)),
    [liveAdsPerformance]
  );

  const {
    rows: exportCampaignRows,
    onBeforeCapture: onBeforeCampaignPdfCapture,
    onAfterCapture: onAfterCampaignPdfCapture,
  } = useDashboardPdfRowOverride(campaignRows, async () => {
    if (activeTab !== "campaigns") {
      return campaignRows;
    }
    return fetchAllPagesForExport({
      pageSize: TABLE_PAGE_SIZE,
      fetchPage: async (page, pageSize) => {
        const sort = toApiSort<GoogleAdsCampaignSort>(
          campaignSort,
          CAMPAIGN_SORT_FIELD,
          "spend_desc"
        );
        const response = await googleAdsDashboardService.fetchCampaigns({
          start_date: dateFilters.appliedStartDate,
          end_date: dateFilters.appliedEndDate,
          lang: apiLang,
          page,
          page_size: pageSize,
          sort: sort ?? "spend_desc",
          search: debouncedSearchCampaigns || null,
          ad_network_type: selectedCampaignType === "all" ? null : selectedCampaignType,
        });
        return {
          rows: (response?.campaigns ?? []).map(mapCampaignApiItem),
          total: response?.meta?.total_items ?? 0,
        };
      },
    });
  });

  const {
    rows: exportAdGroupRows,
    onBeforeCapture: onBeforeAdGroupPdfCapture,
    onAfterCapture: onAfterAdGroupPdfCapture,
  } = useDashboardPdfRowOverride(adGroupRows, async () => {
    if (activeTab !== "adgroups") {
      return adGroupRows;
    }
    return fetchAllPagesForExport({
      pageSize: TABLE_PAGE_SIZE,
      fetchPage: async (page, pageSize) => {
        const sort = toApiSort<GoogleAdsAdGroupSort>(
          adGroupSort,
          AD_GROUP_SORT_FIELD,
          "spend_desc"
        );
        const response = await googleAdsDashboardService.fetchAdGroupPerformance({
          start_date: dateFilters.appliedStartDate,
          end_date: dateFilters.appliedEndDate,
          lang: apiLang,
          page,
          page_size: pageSize,
          sort: sort ?? "spend_desc",
          search: debouncedSearchAdGroups || null,
        });
        return {
          rows: (response?.ad_groups ?? []).map((row, index) => mapAdGroupApiItem(row, index)),
          total: response?.meta?.total_items ?? 0,
        };
      },
    });
  });

  const {
    rows: exportAdRows,
    onBeforeCapture: onBeforeAdsPdfCapture,
    onAfterCapture: onAfterAdsPdfCapture,
  } = useDashboardPdfRowOverride(adRows, async () => {
    if (activeTab !== "ads") {
      return adRows;
    }
    return fetchAllPagesForExport({
      pageSize: TABLE_PAGE_SIZE,
      fetchPage: async (page, pageSize) => {
        const sort = toApiSort<GoogleAdsAdsSort>(adsSort, ADS_SORT_FIELD, "spend_desc");
        const response = await googleAdsDashboardService.fetchPerformanceByAd({
          start_date: dateFilters.appliedStartDate,
          end_date: dateFilters.appliedEndDate,
          lang: apiLang,
          page,
          page_size: pageSize,
          sort: sort ?? "spend_desc",
          search: debouncedSearchAds || null,
        });
        return {
          rows: (response?.ads ?? []).map((row, index) => mapPerformanceByAdItem(row, index)),
          total: response?.meta?.total_items ?? 0,
        };
      },
    });
  });

  const adTypeChart = useMemo(() => {
    const unknownLabel = t("googleAdsReport.campaignChannel.unknown");
    const rows = liveAdNetworkBreakdown.map(row => ({
      label: row.ad_network_type?.trim() || unknownLabel,
      spend: 0,
      impressions: 0,
      clicks: Number(row.clicks ?? 0),
      conversions: Number(row.conversions ?? 0),
      ctr: 0,
    }));
    return normalizeGroupedChartData(rows, "label");
  }, [liveAdNetworkBreakdown, t]);

  const exportAdsCsv = useCallback(async () => {
    setIsExporting(true);
    try {
      const sort = toApiSort<GoogleAdsAdsSort>(adsSort, ADS_SORT_FIELD, "spend_desc");
      const totalPages = Math.max(1, adsPaginationMeta.total_pages || 1);
      const responses = await Promise.all(
        Array.from({ length: totalPages }, (_, i) =>
          googleAdsDashboardService.fetchPerformanceByAd({
            start_date: dateFilters.appliedStartDate,
            end_date: dateFilters.appliedEndDate,
            lang: apiLang,
            page: i + 1,
            page_size: TABLE_PAGE_SIZE,
            sort: sort ?? "spend_desc",
            search: debouncedSearchAds || null,
          })
        )
      );
      const headers = [
        t("googleAdsReport.adsTable.adName"),
        t("googleAdsReport.adsTable.type"),
        t("googleAdsReport.adsTable.spend"),
        t("googleAdsReport.adsTable.impressions"),
        t("googleAdsReport.adsTable.clicks"),
        t("googleAdsReport.adsTable.ctr"),
        t("googleAdsReport.adsTable.cpc"),
        t("googleAdsReport.adsTable.conversions"),
        t("googleAdsReport.adsTable.cpa"),
        t("googleAdsReport.adsTable.roas"),
      ];
      const rows: Array<Array<string | number | null | undefined>> = [];
      responses.flatMap(r => r?.ads ?? []).forEach((item, index) => {
        const row = mapPerformanceByAdItem(item, index);
        rows.push([
          row.name,
          row.type,
          row.spend,
          row.impressions,
          row.clicks,
          row.ctr !== null ? `${(row.ctr * 100).toFixed(2)}%` : "",
          row.cpc !== null ? row.cpc : "",
          row.conversions,
          row.cpa !== null ? row.cpa : "",
          `${row.roas.toFixed(2)}x`,
        ]);
      });
      downloadCsv("google-ads-ads.csv", headers, rows);
    } catch {
      window.alert(t("googleAdsReport.export.failed"));
    } finally {
      setIsExporting(false);
    }
  }, [
    adsPaginationMeta,
    adsSort,
    apiLang,
    dateFilters.appliedEndDate,
    dateFilters.appliedStartDate,
    debouncedSearchAds,
    t,
  ]);

  const roasChartData = useMemo(
    () =>
      liveRoasByCampaign.slice(0, CHART_TOP_ENTRIES).map(item => ({
        name: item.campaign_name,
        roas: Number(item.roas ?? 0),
      })),
    [liveRoasByCampaign]
  );

  const spendByCampaignChart = useMemo(() => {
    if (liveSpendByCampaign.length > 0) {
      return liveSpendByCampaign.map(item => ({
        name: item.campaign_name,
        spend: normalizeGoogleAdsMoneyValue(item.spend).amount,
        spendFormattedValue: item.spendFormattedValue,
      }));
    }

    return campaignRows.map(row => ({
      name: row.campaignName,
      spend: row.spend ?? 0,
      spendFormattedValue: undefined as string | undefined,
    }));
  }, [campaignRows, liveSpendByCampaign]);

  const campaignTotalPages = Math.max(1, campaignPaginationMeta.total_pages || 1);
  const adGroupTotalPages = Math.max(1, adGroupPaginationMeta.total_pages || 1);
  const adsPageCount = Math.max(1, adsPaginationMeta.total_pages || 1);

  const toggleCampaignSort = (key: GoogleAdsCampaignSortKey) => {
    setCurrentCampaignPage(1);
    setCampaignSort(s => toggleTableSort(s, key));
  };
  const toggleAdGroupSort = (key: GoogleAdsAdGroupSortKey) => {
    setCurrentAdGroupPage(1);
    setAdGroupSort(s => toggleTableSort(s, key));
  };
  const toggleAdsSort = (key: GoogleAdsAdsSortKey) => {
    setAdsPage(0);
    setAdsSort(s => toggleTableSort(s, key));
  };

  const toggleMetric = (metric: MetricKey) => {
    setActiveMetrics(current => {
      if (current.includes(metric)) {
        return current.length === 1 ? current : current.filter(item => item !== metric);
      }

      return [...current, metric];
    });
  };

  const dateRangeError =
    dateFilters.appliedStartDate > dateFilters.appliedEndDate
      ? t("googleAdsReport.errors.dateRange")
      : "";

  const narrativeData = useMemo(
    () => ({
      kpi: liveKpis,
      performance_trends: livePerformanceTrends,
      roas_by_campaign: liveRoasByCampaign,
      campaigns: liveCampaigns,
      ad_group_performance: liveAdGroups,
      ad_group_charts: liveAdGroupChartPerformance,
      ad_network_breakdown: liveAdNetworkBreakdown,
      performance_by_ad: liveAdsPerformance,
    }),
    [liveKpis, livePerformanceTrends, liveRoasByCampaign, liveCampaigns, liveAdGroups, liveAdGroupChartPerformance, liveAdNetworkBreakdown, liveAdsPerformance]
  );

  const isLoading =
    isKpiLoading ||
    isTrendsLoading ||
    isCampaignLoading ||
    isChartsLoading;
  const isTableLoading =
    (activeTab === "adgroups" && isAdGroupLoading) ||
    (activeTab === "ads" && isAdsLoading);

  // Snapshot the payload exactly once per date-range load cycle so late-arriving
  // data (charts, ad groups, etc.) does not re-trigger the narrative API.
  const isGooglePrimaryLoading = isKpiLoading || isTrendsLoading || isCampaignLoading || isChartsLoading;
  const googleNarrativeDateKey = `${dateFilters.appliedStartDate}|${dateFilters.appliedEndDate}`;
  const googleNarrativePrevIsLoadingRef = useRef(true);
  const googleNarrativeCommittedKeyRef = useRef("");
  const [googleLockedNarrativeData, setGoogleLockedNarrativeData] = useState<Record<string, unknown> | null>(null);

  useEffect(() => {
    googleNarrativeCommittedKeyRef.current = "";
    setGoogleLockedNarrativeData(null);
  }, [dateFilters.appliedStartDate, dateFilters.appliedEndDate]);

  useEffect(() => {
    const wasLoading = googleNarrativePrevIsLoadingRef.current;
    googleNarrativePrevIsLoadingRef.current = isGooglePrimaryLoading;
    if (dateRangeError) {
      googleNarrativeCommittedKeyRef.current = "";
      setGoogleLockedNarrativeData(null);
      return;
    }
    if (wasLoading && !isGooglePrimaryLoading && googleNarrativeCommittedKeyRef.current !== googleNarrativeDateKey) {
      googleNarrativeCommittedKeyRef.current = googleNarrativeDateKey;
      setGoogleLockedNarrativeData(narrativeData);
    }
  }, [isGooglePrimaryLoading, dateRangeError, googleNarrativeDateKey, narrativeData]);

  const { narrative: googleNarrative, loading: narrativeLoading, aiSummary } = useDashboardNarrative({
    dashboardKey: "paid_media",
    tabKey: "google_ads_report",
    startDate: dateFilters.appliedStartDate,
    endDate: dateFilters.appliedEndDate,
    data: googleLockedNarrativeData ?? {},
    enabled: googleLockedNarrativeData !== null && !dateRangeError,
    promptVersion: "v6",
  });

  return (
    <section
      className={`paid-media-report google-ads-report${isPdfCapturing ? " paid-media-report--pdf-capture" : ""}`}
      ref={pdfContainerRef}
    >
      <div className="paid-media-report__header-block">
        <DashboardSectionHeader
          className="paid-media-report__section-header"
          divider={false}
          title={t("googleAdsReport.title")}
          actions={
            <>
              <div className="dashboard-section-header__control dashboard-section-header__control--wide">
                <OrganicDateRangePicker
                  startDate={dateFilters.draftStartDate}
                  endDate={dateFilters.draftEndDate}
                  onRangeChange={dateFilters.onApplyDates}
                />
              </div>
              <DashboardExportButton
                dashboardName="Google Ads"
                dateRange={{
                  startDate: dateFilters.appliedStartDate,
                  endDate: dateFilters.appliedEndDate,
                }}
                containerRef={pdfContainerRef}
                disabled={isLoading || Boolean(dateRangeError)}
                onBeforeCapture={async () => {
                  setIsPdfCapturing(true);
                  if (activeTab === "campaigns") {
                    await onBeforeCampaignPdfCapture();
                  } else if (activeTab === "adgroups") {
                    await onBeforeAdGroupPdfCapture();
                  } else if (activeTab === "ads") {
                    await onBeforeAdsPdfCapture();
                  }
                }}
                onAfterCapture={() => {
                  onAfterCampaignPdfCapture();
                  onAfterAdGroupPdfCapture();
                  onAfterAdsPdfCapture();
                  setIsPdfCapturing(false);
                }}
              />
            </>
          }
        />
        <div className="paid-media-report__subtitle-toolbar">
          <p className="dashboard-section-header__subtitle paid-media-report__header-subtitle">
            {t("googleAdsReport.subtitle")}
          </p>
        </div>
      </div>

      <div className="paid-media-report__content">
        {dateRangeError ? <DashboardErrorState message={dateRangeError} /> : null}
        {!dateRangeError && kpiApiError ? <DashboardErrorState message={kpiApiError} /> : null}
        {!dateRangeError && trendsApiError ? (
          <DashboardErrorState message={trendsApiError} />
        ) : null}
        {!dateRangeError && adGroupApiError && activeTab === "adgroups" ? (
          <DashboardErrorState message={adGroupApiError} />
        ) : null}
        {!dateRangeError && campaignApiError && activeTab === "campaigns" ? (
          <DashboardErrorState message={campaignApiError} />
        ) : null}
        {!dateRangeError && adsApiError && activeTab === "ads" ? (
          <DashboardErrorState message={adsApiError} />
        ) : null}
        {!dateRangeError && chartsApiError ? (
          <DashboardErrorState message={chartsApiError} />
        ) : null}
        {!dateRangeError && isLoading ? (
          <div className="paid-media-report__loading">
            <LoadingSpinner compact />
          </div>
        ) : null}

        {!dateRangeError && !isLoading ? (
          <>
            <div data-pdf-block="narrative">
              <DashboardNarrativePanel
                narrative={googleNarrative}
                loading={narrativeLoading}
                aiSummary={aiSummary}
              />
            </div>
            <div data-pdf-block="kpi-cards">
            <GoogleKpiGrid
              kpis={kpis}
              kpiIcons={googleKpiIcons}
              getGoogleKpiTooltip={getGoogleKpiTooltip}
            />
            </div>

            <div className="paid-media-overview-grid" data-pdf-block="google-trends-funnel">
              <GooglePerformanceTrendsPanel
                googleSectionHelp={googleSectionHelp}
                trendGranularity={trendGranularity}
                setTrendGranularity={setTrendGranularity}
                activeMetrics={activeMetrics}
                toggleMetric={toggleMetric}
                paginatedTrendData={paginatedTrendData}
                hasSinglePerformanceTrendPoint={hasSinglePerformanceTrendPoint}
                formatDateShortChart={formatDateShortChart}
                formatChartAxisCompactLocalized={formatChartAxisCompactLocalized}
                formatChartMetricTooltipValue={formatChartMetricTooltipValue}
                currentTrendPage={currentTrendPage}
                setCurrentTrendPage={setCurrentTrendPage}
                trendTotalPages={trendTotalPages}
              />

              <GoogleConversionFunnelPanel
                googleSectionHelp={googleSectionHelp}
                funnelSteps={funnelSteps}
                formatCurrency={formatCurrency}
                formatConversionMetric={formatConversionMetric}
                formatCountMetric={formatCountMetric}
              />
            </div>

            <GoogleChartsRow1
              googleSectionHelp={googleSectionHelp}
              roasChartData={roasChartData}
              adGroupChartData={adGroupChartData}
              adGroupSpendPieData={adGroupSpendPieData}
              clicksConversionsLegendItems={clicksConversionsLegendItems}
              spendLegendItems={spendLegendItems}
              barSeriesLabels={barSeriesLabels}
              formatChartAxisCompactLocalized={formatChartAxisCompactLocalized}
              formatCountMetric={formatCountMetric}
              formatConversionMetric={formatConversionMetric}
              formatCurrency={formatCurrency}
              numberLocale={numberLocale}
            />

            <GoogleChartsRow2
              googleSectionHelp={googleSectionHelp}
              adTypeChart={adTypeChart}
              spendByCampaignChart={spendByCampaignChart}
              clicksConversionsLegendItems={clicksConversionsLegendItems}
              barSeriesLabels={barSeriesLabels}
              formatCountMetric={formatCountMetric}
              formatConversionMetric={formatConversionMetric}
              formatCurrency={formatCurrency}
              numberLocale={numberLocale}
            />

            <div data-pdf-block="ads-table" data-pdf-splittable>
            <div className="paid-media-tabs-bar">
              {GOOGLE_ADS_TAB_IDS.map(tabId => (
                <button
                  key={tabId}
                  type="button"
                  className={
                    activeTab === tabId
                      ? "paid-media-tabs-bar__tab paid-media-tabs-bar__tab--active"
                      : "paid-media-tabs-bar__tab"
                  }
                  onClick={() => setActiveTab(tabId)}
                >
                  {googleOverviewTabLabel(tabId)}
                </button>
              ))}
            </div>

            {isTableLoading ? (
              <div className="paid-media-report__loading">
                <LoadingSpinner compact />
              </div>
            ) : null}

            {!isTableLoading && activeTab === "campaigns" ? (
              <GoogleCampaignsTable
                googleSectionHelp={googleSectionHelp}
                selectedCampaignType={selectedCampaignType}
                setSelectedCampaignType={setSelectedCampaignType}
                campaignTypeFilterOptions={campaignTypeFilterOptions}
                searchCampaigns={searchCampaigns}
                setSearchCampaigns={setSearchCampaigns}
                setCurrentCampaignPage={setCurrentCampaignPage}
                isExporting={isExporting}
                exportCampaignsCsv={exportCampaignsCsv}
                toggleCampaignSort={toggleCampaignSort}
                campaignRows={exportCampaignRows}
                expandedCampaigns={expandedCampaigns}
                toggleCampaignExpanded={toggleCampaignExpanded}
                formatCountMetric={formatCountMetric}
                formatNullableCurrency={formatNullableCurrency}
                formatNullablePercent={formatNullablePercent}
                formatConversionMetric={formatConversionMetric}
                formatNullableRoas={formatNullableRoas}
                formatCurrency={formatCurrency}
                formatCurrencyShort={formatCurrencyShort}
                currentCampaignPage={currentCampaignPage}
                campaignTotalPages={campaignTotalPages}
              />
            ) : null}

            {!isTableLoading && activeTab === "adgroups" ? (
              <GoogleAdGroupsTable
                googleSectionHelp={googleSectionHelp}
                adGroupSearch={adGroupSearch}
                setAdGroupSearch={setAdGroupSearch}
                setCurrentAdGroupPage={setCurrentAdGroupPage}
                isExporting={isExporting}
                exportAdGroupsCsv={exportAdGroupsCsv}
                toggleAdGroupSort={toggleAdGroupSort}
                adGroupRows={exportAdGroupRows}
                formatCountMetric={formatCountMetric}
                formatCurrency={formatCurrency}
                formatNullablePercent={formatNullablePercent}
                formatCurrencyShort={formatCurrencyShort}
                formatConversionMetric={formatConversionMetric}
                currentAdGroupPage={currentAdGroupPage}
                adGroupTotalPages={adGroupTotalPages}
              />
            ) : null}

            {!isTableLoading && activeTab === "ads" ? (
              <GoogleAdsTable
                googleSectionHelp={googleSectionHelp}
                adsSearch={adsSearch}
                setAdsSearch={setAdsSearch}
                setAdsPage={setAdsPage}
                isExporting={isExporting}
                exportAdsCsv={exportAdsCsv}
                toggleAdsSort={toggleAdsSort}
                adRows={exportAdRows}
                formatCountMetric={formatCountMetric}
                formatConversionMetric={formatConversionMetric}
                formatNullablePercent={formatNullablePercent}
                adsPage={adsPage}
                adsPageCount={adsPageCount}
              />
            ) : null}

            {activeTab === "alerts" ? <GoogleAlertsPanel /> : null}
            </div>
          </>
        ) : null}
      </div>
    </section>
  );
};

export default GoogleAdsReport;
